import {
  useIsRestoring,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import { useAccessToken } from "@/hooks";
import type { GetClassResponse } from "@/openapi/generated";
import {
  classesByIdsQueryKey,
  fetchAndSeedClassesByIds,
  uniqueSortedClassIds,
} from "./classes-by-ids-query";

interface UseClassesGetByIdsProps {
  classIds: number[];
  enabled?: boolean;
}

export const useClassesGetByIds = ({
  classIds,
  enabled = true,
}: UseClassesGetByIdsProps) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const isRestoring = useIsRestoring();

  const stableClassIds = useMemo(
    () => uniqueSortedClassIds(classIds),
    [classIds],
  );

  const isEnabled =
    enabled && stableClassIds.length > 0 && !!accessToken && !isRestoring;

  const {
    data,
    isPending,
    isError: hasError,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: classesByIdsQueryKey(stableClassIds),
    queryFn: async () => {
      if (!accessToken) {
        throw new Error("Authentication token is required");
      }
      return fetchAndSeedClassesByIds(queryClient, accessToken, stableClassIds);
    },
    enabled: isEnabled,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const classesMap = useMemo(() => {
    const map = new Map<number, GetClassResponse>();
    for (const classItem of data ?? []) {
      const classId = Number(classItem.classId);
      if (Number.isFinite(classId) && classId > 0) {
        map.set(classId, classItem);
      }
    }
    return map;
  }, [data]);

  const refetchAll = () => refetch();

  return {
    classesMap,
    isPending: isEnabled && isPending,
    hasError,
    isFetching,
    refetchAll,
  };
};
